import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Save, Loader2, BarChart3, DollarSign, TrendingUp, Target, Clock, Presentation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import LeiImpactCard from '../components/LeiImpactCard';
import { WaterfallChart } from '../components/charts/WaterfallChart';
import { ROIDistributionChart } from '../components/charts/ROIDistributionChart';
import { SensitivityTornado } from '../components/charts/SensitivityTornado';
import { MaturityGauge } from '../components/charts/MaturityGauge';
import { useApp } from '../context/AppContext';
import { saveScenario } from '../lib/api';
import { formatCurrency, getROIStatus } from '../lib/utils';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

export const ResultsPage = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const {
    configuration,
    results,
    getSelectedLEIMeasure,
    getSelectedKPIs,
    currentScenario,
    currency,
  } = useApp();

  const selectedLEI = getSelectedLEIMeasure();
  const selectedKPIs = getSelectedKPIs();
  const selectedKPINames = selectedKPIs?.map(k => k.name).join(', ') || 'None';

  // /results is a flow page — it reads from in-memory React context
  // populated only after running an analysis from /configure. When the
  // user lands here directly (refresh, deep link, sidebar nav), bounce
  // them back to /configure rather than showing an empty-state dead
  // end. This preserves the demo flow integrity.
  useEffect(() => {
    if (!results) {
      const t = setTimeout(() => navigate('/configure', { replace: true }), 800);
      return () => clearTimeout(t);
    }
  }, [results, navigate]);

  // Navigate to Board Ready for exports (PDF, Word, PPT)
  const handleBoardReady = async () => {
    // First save the scenario if not already saved
    if (!currentScenario?.id) {
      setIsSaving(true);
      try {
        const scenarioName = `${selectedLEI?.name || 'Analysis'} → ${selectedKPIs?.length || 0} KPIs (${new Date().toLocaleDateString()})`;
        const response = await saveScenario({
          name: scenarioName,
          description: `${configuration.lei_improvement}% improvement analysis`,
          configuration,
          results,
          analysis_type: 'single',
        });
        toast.success('Scenario saved!');
        // Navigate to Board Ready with the new scenario ID
        navigate(`/board-ready/${response.data.scenario_id}`);
      } catch (error) {
        logger.error('Save error:', error);
        toast.error('Failed to save scenario. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      // Navigate to Board Ready with existing scenario ID
      navigate(`/board-ready/${currentScenario.id}`);
    }
  };

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="results-page-empty">
        <p className="text-slate-500 mb-4">Redirecting to configure an analysis…</p>
        <Button
          onClick={() => navigate('/configure', { replace: true })}
          className="bg-navy-700 hover:bg-navy-800"
          data-testid="results-empty-configure-btn"
        >
          Configure Analysis
        </Button>
      </div>
    );
  }

  const handleSaveScenario = async () => {
    try {
      const scenarioName = `${selectedLEI?.name} → ${selectedKPIs?.length || 0} KPIs (${new Date().toLocaleDateString()})`;
      await saveScenario({
        name: scenarioName,
        description: `${configuration.lei_improvement}% improvement analysis`,
        configuration,
        results,
      });
      toast.success('Scenario saved successfully');
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save scenario');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const __pageTheme = getLightSectionTheme('Navy');
  const resultsHeroActions = (
    <>
      <Button
        variant="outline"
        onClick={() => navigate('/configure')}
        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="back-to-config-btn"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
      <Button
        variant="outline"
        onClick={handleShare}
        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="share-btn"
      >
        <Share2 className="mr-2 h-4 w-4" />
        Share
      </Button>
      <Button
        variant="outline"
        onClick={handleSaveScenario}
        className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="save-scenario-btn"
      >
        <Save className="mr-2 h-4 w-4" />
        Save
      </Button>
      <Button
        onClick={handleBoardReady}
        disabled={isSaving}
        className={__pageTheme.actionBtn}
        data-testid="board-ready-btn"
      >
        {isSaving ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Presentation className="mr-2 h-4 w-4" />
        )}
        Board Ready
      </Button>
    </>
  );
  const __r_kpiDelta = results.kpi_delta_percent || results.total_kpi_delta_percent || 0;
  const __r_finImpact = results.financial_impact || results.total_financial_impact || 0;
  const __r_netBenefit = results.net_benefit || results.total_net_benefit || 0;
  const __r_roiPct = results.roi_percent || results.total_roi_percent || 0;
  const resultsHeroStats = [
    {
      label: 'KPI Uplift',
      value: `${__r_kpiDelta.toFixed(1)}%`,
      testId: 'results-stat-kpi-uplift',
      whyPanel: {
        label: 'KPI Uplift',
        value: `${__r_kpiDelta.toFixed(1)}%`,
        description: 'Projected percentage improvement on the selected KPIs from baseline, driven by the LEI improvement target you configured for this scenario.',
        formula: 'sum(KPI_baseline × elasticity × LEI_improvement) ÷ sum(KPI_baseline)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Single-digit uplift on a high-baseline KPI can deliver more value than a 50% uplift on a tiny one. Always read this together with Financial Impact.' },
          { label: 'What to do', value: '', hint: __r_kpiDelta === 0
            ? 'Uplift is 0% — check that elasticities are non-zero in Custom Measures and that an LEI improvement target is set in Configuration.'
            : __r_kpiDelta > 50
              ? 'Uplift looks aggressive. Re-validate elasticities — over-elastic KPIs are the most common source of inflated business cases.'
              : 'Realistic uplift. Save this scenario, then come back in 90 days to compare against actuals via Gap Analysis.' },
        ],
        links: [
          { label: 'Tune elasticities', path: '/custom-measures' },
        ],
      },
    },
    {
      label: 'Financial Impact',
      value: formatCurrency(__r_finImpact, currency),
      testId: 'results-stat-financial-impact',
      whyPanel: {
        label: 'Financial Impact',
        value: formatCurrency(__r_finImpact, currency),
        description: 'Gross monetary value created by the projected KPI uplift, before subtracting program cost. This is the top-line revenue/cost-savings figure to put in front of finance.',
        formula: 'sum(KPI_uplift × KPI_value_per_unit × population)',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Gross impact tells the upside story. Always pair it with Net Benefit so the audience sees the cost-adjusted view too.' },
          { label: 'What to do', value: '', hint: 'Use the export to take this number into your CFO deck. Pin the scenario for re-use in Quarterly Deck.' },
        ],
        links: [
          { label: 'Open Quarterly Deck', path: '/' },
        ],
      },
    },
    {
      label: 'Net Benefit',
      value: formatCurrency(__r_netBenefit, currency),
      testId: 'results-stat-net-benefit',
      whyPanel: {
        label: 'Net Benefit',
        value: formatCurrency(__r_netBenefit, currency),
        description: 'Financial Impact minus the total cost of the program (delivery + opportunity). This is the number your CFO will weigh against alternative investments.',
        formula: 'Financial Impact − Program Cost',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Negative net benefit is a red flag — the program isn\'t earning its keep. Positive but small relative to other initiatives means re-prioritise budget.' },
          { label: 'What to do', value: '', hint: __r_netBenefit <= 0
            ? 'Net benefit is non-positive. Either reduce program cost (delivery model) or re-target higher-value KPIs in Configuration.'
            : 'Healthy. Compare net benefit across scenarios in Saved Scenarios to find the highest-leverage option before committing budget.' },
        ],
        links: [
          { label: 'Open Saved Scenarios', path: '/scenarios' },
        ],
      },
    },
    {
      label: 'ROI',
      value: `${(__r_roiPct / 100).toFixed(1)}x`,
      testId: 'results-stat-roi',
      whyPanel: {
        label: 'ROI Multiple',
        value: `${(__r_roiPct / 100).toFixed(1)}x`,
        description: 'Return per dollar invested — the headline metric finance teams use to compare initiatives. 2.0x means $1 of program cost returns $2 of benefit.',
        formula: '(Financial Impact − Program Cost) ÷ Program Cost + 1',
        breakdown: [
          { label: 'How to consider it', value: '', hint: 'Most credible L&D scenarios fall in the 1.5x–4x band. Above 5x almost always means an elasticity is too high or a cost is missing — re-check before publishing.' },
          { label: 'What to do', value: '', hint: __r_roiPct < 100
            ? 'ROI is below break-even. Re-tune elasticities, expand the population, or trim cost in Configuration.'
            : __r_roiPct > 500
              ? 'ROI looks too good. Have a peer pressure-test the elasticities in Custom Measures before sharing.'
              : 'Defensible band. Pin this scenario to Quarterly Deck and compare against actuals quarterly via Gap Analysis.' },
        ],
        links: [
          { label: 'Pin to Quarterly Deck', path: '/scenarios' },
          { label: 'Compare vs actuals', path: '/gap-analysis' },
        ],
      },
    },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title={currentScenario?.name || 'Analysis Results'}
        description={currentScenario?.description || `${selectedLEI?.name || 'Portfolio Analysis'} → ${selectedKPIs?.length || results?.kpi_results?.length || 0} KPIs | ${configuration.lei_improvement}% improvement target`}
        theme={__pageTheme}
        icon={BarChart3}
        actions={resultsHeroActions}
        stats={resultsHeroStats}
        testId="results-hero"
      />
    )}>
    <div className="space-y-8 animate-fade-in">

      {/* KPI Summary Tiles - Colorful rounded bottom style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* KPI Uplift - Teal */}
        <div className="bg-gradient-to-b from-teal-500 to-teal-600 rounded-t-2xl rounded-b-[2rem] p-5 text-white shadow-lg min-h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-2 text-slate-900 text-sm font-medium uppercase tracking-wide">
            <BarChart3 className="h-4 w-4" />
            <span>KPI Uplift</span>
          </div>
          <div>
            <div className="text-3xl font-bold mt-2">
              {(results.kpi_delta_percent || results.total_kpi_delta_percent || 0).toFixed(2)}%
            </div>
            <p className="text-slate-600 text-sm mt-1">Projected improvement</p>
            {(() => {
              const status = getROIStatus(results.roi_percent || results.total_roi_percent || 0);
              return status?.label && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-white border border-slate-200">
                  {status.label}
                </span>
              );
            })()}
          </div>
        </div>
        
        {/* Financial Impact - Blue */}
        <div className="bg-gradient-to-b from-blue-500 to-blue-600 rounded-t-2xl rounded-b-[2rem] p-5 text-white shadow-lg min-h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-2 text-slate-900 text-sm font-medium uppercase tracking-wide">
            <DollarSign className="h-4 w-4" />
            <span>Financial Impact</span>
          </div>
          <div>
            <div className="text-3xl font-bold mt-2">
              {formatCurrency(Math.round(results.financial_impact || results.total_financial_impact || 0), currency)}
            </div>
            <p className="text-slate-600 text-sm mt-1">Annual value</p>
            {(results.financial_impact || results.total_financial_impact || 0) > 0 && (
              <span className="inline-flex items-center gap-1 mt-2 text-slate-700 text-xs">
                <TrendingUp className="h-3 w-3" /> Positive
              </span>
            )}
          </div>
        </div>
        
        {/* Net Benefit - Green */}
        <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-t-2xl rounded-b-[2rem] p-5 text-white shadow-lg min-h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-2 text-slate-900 text-sm font-medium uppercase tracking-wide">
            <TrendingUp className="h-4 w-4" />
            <span>Net Benefit</span>
          </div>
          <div>
            <div className="text-3xl font-bold mt-2">
              {formatCurrency(Math.round(results.net_benefit || results.total_net_benefit || 0), currency)}
            </div>
            <p className="text-slate-600 text-sm mt-1">After investment costs</p>
          </div>
        </div>
        
        {/* ROI - Purple */}
        <div className="bg-gradient-to-b from-purple-500 to-purple-600 rounded-t-2xl rounded-b-[2rem] p-5 text-white shadow-lg min-h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-2 text-slate-900 text-sm font-medium uppercase tracking-wide">
            <Target className="h-4 w-4" />
            <span>ROI</span>
          </div>
          <div>
            <div className="text-3xl font-bold mt-2">
              {((results.roi_percent || results.total_roi_percent || 0) / 100).toFixed(1)}x
            </div>
            <p className="text-slate-600 text-sm mt-1">Return on investment</p>
            {(() => {
              const status = getROIStatus(results.roi_percent || results.total_roi_percent || 0);
              return status?.label && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-white border border-slate-200">
                  {status.label}
                </span>
              );
            })()}
          </div>
        </div>
        
        {/* Payback Period - Orange */}
        <div className="bg-gradient-to-b from-orange-400 to-orange-500 rounded-t-2xl rounded-b-[2rem] p-5 text-white shadow-lg min-h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-2 text-slate-900 text-sm font-medium uppercase tracking-wide">
            <Clock className="h-4 w-4" />
            <span>Payback Period</span>
          </div>
          <div>
            <div className="text-3xl font-bold mt-2">
              {(results.payback_months || Math.ceil((configuration.intervention_cost || 100000) / ((results.financial_impact || results.total_financial_impact || 1) / 12))).toFixed(1)} mo
            </div>
            <p className="text-slate-600 text-sm mt-1">Time to break even</p>
          </div>
        </div>
      </div>

      {/* Causal Component Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Causal Component Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500 mb-1">Influence Weight</p>
              <p className="text-2xl font-bold text-navy-700">
                {results.weight?.toFixed(2) || (results.kpi_results?.[0]?.weight?.toFixed(2)) || 'Avg'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Directional strength</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500 mb-1">Elasticity</p>
              <p className="text-2xl font-bold text-teal-600">
                {results.elasticity?.toFixed(2) || (results.kpi_results?.[0]?.elasticity?.toFixed(2)) || 'Var'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Marginal sensitivity</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500 mb-1">Dampening</p>
              <p className="text-2xl font-bold text-amber-600">{results.dampening?.toFixed(2) || '0.74'}</p>
              <p className="text-xs text-slate-400 mt-1">Execution realism</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-500 mb-1">Maturity Factor</p>
              <p className="text-2xl font-bold text-slate-700">{results.maturity_factor?.toFixed(2) || '1.00'}</p>
              <p className="text-xs text-slate-400 mt-1">Diminishing returns</p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-navy-50 rounded-lg">
            <p className="text-sm text-navy-800 font-mono">
              ΔKPI = ΔLEI × Weight × Elasticity × Dampening × MaturityFactor
            </p>
            <p className="text-sm text-navy-600 font-mono mt-2">
              {(results.kpi_delta_percent || results.total_kpi_delta_percent || 0)?.toFixed(3)}% = {configuration.lei_improvement}% × W × E × {results.dampening?.toFixed(2) || '0.74'} × {results.maturity_factor?.toFixed(2) || '1.00'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Monte Carlo Results - only show if data available */}
      {(results.roi_mean !== undefined || results.roi_p5 !== undefined) && (
        <Card className="border-teal-200 bg-teal-50/30">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Monte Carlo Simulation (5,000 iterations)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">Mean ROI</p>
                <p className="text-2xl font-bold text-slate-900">{((results.roi_mean || 0) / 100)?.toFixed(1)}x</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">5th Percentile</p>
                <p className="text-2xl font-bold text-rose-600">{((results.roi_p5 || 0) / 100)?.toFixed(1)}x</p>
                <p className="text-xs text-slate-400">Downside</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">95th Percentile</p>
                <p className="text-2xl font-bold text-emerald-600">{((results.roi_p95 || 0) / 100)?.toFixed(1)}x</p>
                <p className="text-xs text-slate-400">Upside</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">P(ROI &gt; 0)</p>
                <p className="text-2xl font-bold text-slate-900">{(results.prob_positive_roi || 0)?.toFixed(0)}%</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                <p className="text-sm text-slate-500">P(ROI &gt; 100%)</p>
                <p className="text-2xl font-bold text-slate-900">{(results.prob_roi_over_100 || 0)?.toFixed(0)}%</p>
              </div>
            </div>
            {(() => {
              const width = Math.abs((results.roi_p95 || 0) - (results.roi_p5 || 0));
              const capped = (results.warnings || []).some((w) => /cap/i.test(w)) || (results.kpi_delta_percent >= 0.3);
              if (width > 10) return null;
              return (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-teal-300 bg-teal-100/70 px-3 py-2.5 text-sm text-teal-900" data-testid="mc-tight-band-note">
                  <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-teal-600 shrink-0" />
                  <span><b>High confidence — not a glitch.</b> The simulated ROI band is very tight ({(width / 100).toFixed(2)}x spread), so the outcome is near-certain across all 5,000 runs{capped ? '. The KPI uplift is pinned at the model ceiling (0.3% per 1% LEI improvement), which removes upside variance' : ''}. A narrow band means low uncertainty, not a broken simulation.</span>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Risk Profile - only show if Monte Carlo data available */}
      {(results.roi_p5 !== undefined && results.roi_p95 !== undefined) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Causal Components</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { label: 'Influence Weight', value: results.weight, color: 'text-navy-700', desc: 'Directional strength of LEI-KPI relationship' },
                { label: 'Elasticity', value: results.elasticity, color: 'text-teal-600', desc: 'Marginal sensitivity coefficient' },
                { label: 'Dampening Factor', value: results.dampening, color: 'text-amber-600', desc: 'Execution realism adjustment' },
                { label: 'Maturity Factor', value: results.maturity_factor, color: 'text-slate-700', desc: 'Diminishing returns adjustment' },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between p-3 border-b border-slate-100">
                  <div>
                    <p className="font-medium text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </div>
                  <p className={`text-xl font-bold ${item.color}`}>{item.value?.toFixed(2) || 'N/A'}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Risk Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700">Upside Scenario (P95)</p>
                <p className="text-3xl font-bold text-emerald-600">{((results.roi_p95 || 0) / 100)?.toFixed(1)}x ROI</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">Base Case (Mean)</p>
                <p className="text-3xl font-bold text-slate-900">{((results.roi_mean || 0) / 100)?.toFixed(1)}x ROI</p>
              </div>
              <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                <p className="text-sm text-rose-700">Downside Scenario (P5)</p>
                <p className="text-3xl font-bold text-rose-600">{((results.roi_p5 || 0) / 100)?.toFixed(1)}x ROI</p>
              </div>
              <p className="text-sm text-slate-500 text-center">
                Probability of positive ROI: {(results.prob_positive_roi || 0)?.toFixed(0)}%
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Projected LEI impact */}
      {results.lei_impact && (
        <div className="mb-4"><LeiImpactCard leiImpact={results.lei_impact} testId="single-lei-impact" /></div>
      )}

      {/* Multi-KPI Results Table - only show if kpi_results available */}
      {results.kpi_results && results.kpi_results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading">Impact by KPI</CardTitle>
            <CardDescription>Detailed breakdown across all selected KPIs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">KPI</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Weight</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Elasticity</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">KPI Uplift</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Financial Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {results.kpi_results.map((kr, idx) => (
                    <tr key={kr.kpi_id} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                      <td className="py-3 px-4 font-medium text-slate-900">{kr.kpi_name}</td>
                      <td className="py-3 px-4 text-right font-mono">{kr.weight?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono">{kr.elasticity?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-teal-600">{kr.kpi_delta_percent?.toFixed(3)}%</td>
                      <td className="py-3 px-4 text-right font-mono text-teal-600 font-semibold">
                        {formatCurrency(kr.financial_impact || 0, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100">
                    <td className="py-3 px-4 font-bold text-slate-900" colSpan="3">Total</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-teal-700">
                      {(results.total_kpi_delta_percent || 0).toFixed(3)}%
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-teal-700">
                      {formatCurrency(results.total_financial_impact || 0, currency)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Grid - only show for Monte Carlo results (old single-KPI format) */}
      {results.waterfall_data && results.roi_distribution && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <WaterfallChart data={results.waterfall_data} />
          <ROIDistributionChart
            data={results.roi_distribution}
            meanROI={results.roi_mean}
            p5ROI={results.roi_p5}
            p95ROI={results.roi_p95}
            probPositive={results.prob_positive_roi}
            capped={(results.warnings || []).some((w) => /cap/i.test(w)) || (results.kpi_delta_percent >= 0.3)}
          />
        </div>
      )}

      {/* Sensitivity & Maturity - only show for Monte Carlo results */}
      {results.sensitivity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SensitivityTornado
            sensitivity={results.sensitivity}
            baseROI={results.roi_percent}
          />
          <MaturityGauge maturity={configuration.current_maturity} />
        </div>
      )}

      {/* Warnings */}
      {results.warnings && results.warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-lg text-amber-800">Analysis Warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {results.warnings.map((warning, idx) => (
                <li key={`warn-${warning.slice(0, 30)}-${idx}`} className="flex items-start gap-2 text-amber-700">
                  <span className="text-amber-500">•</span>
                  {warning}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
    </PageShell>
  );
};

export default ResultsPage;
